import { Link } from "react-router-dom";
import Header from "../components/header";
import DefaultFooter from "../components/default-footer";

// ============================================================
// Small reusable piece — same dune-silhouette rule used across
// the site, so this page doesn't read as a bolted-on error screen.
// ============================================================
function DuneRule({ tone = "light" }: { tone?: "light" | "dark" }) {
  const stroke = tone === "dark" ? "rgba(255,255,255,.14)" : "rgba(41,75,90,.14)";
  return (
    <div aria-hidden="true" style={{ lineHeight: 0, background: "inherit" }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path
          d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16"
          fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5"
        />
        <path
          d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22"
          fill="none" stroke={stroke} strokeWidth="1"
        />
      </svg>
    </div>
  );
}

// A compass — ties to "you've wandered off the map," fits a travel
// agency's world better than a generic broken-robot illustration.
function CompassIllustration() {
  return (
    <svg width="180" height="180" viewBox="0 0 200 200" fill="none" aria-hidden="true">
      <circle cx="100" cy="100" r="92" stroke="var(--gold)" strokeOpacity="0.35" strokeWidth="1.5" />
      <circle cx="100" cy="100" r="72" stroke="var(--gold)" strokeOpacity="0.5" strokeWidth="1" />
      {[...Array(24)].map((_, i) => {
        const angle = (i * 360) / 24;
        const isMajor = i % 6 === 0;
        const r1 = isMajor ? 72 : 78;
        const r2 = 84;
        const rad = (angle * Math.PI) / 180;
        return (
          <line
            key={i}
            x1={100 + r1 * Math.sin(rad)}
            y1={100 - r1 * Math.cos(rad)}
            x2={100 + r2 * Math.sin(rad)}
            y2={100 - r2 * Math.cos(rad)}
            stroke="var(--gold)"
            strokeOpacity={isMajor ? 0.7 : 0.3}
            strokeWidth={isMajor ? 1.5 : 1}
          />
        );
      })}
      <text x="100" y="34" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--gold)" fontFamily="DM Sans, sans-serif">N</text>
      <text x="100" y="176" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--gold-d)" fontFamily="DM Sans, sans-serif">S</text>
      <text x="30" y="105" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--gold-d)" fontFamily="DM Sans, sans-serif">W</text>
      <text x="170" y="105" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--gold-d)" fontFamily="DM Sans, sans-serif">E</text>
      {/* Needle, pointed a little off true-north — "you've drifted" */}
      <g transform="rotate(28 100 100)">
        <polygon points="100,42 108,100 100,108 92,100" fill="var(--gold)" />
        <polygon points="100,158 108,100 100,92 92,100" fill="var(--teal-l)" />
      </g>
      <circle cx="100" cy="100" r="7" fill="var(--teal-d)" stroke="var(--gold)" strokeWidth="1.5" />
    </svg>
  );
}

function NotFound() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
        }
        .notfound-page { font-family: 'DM Sans', sans-serif; background: var(--cream); }
        .notfound-page h1, .notfound-page h2 { font-family: 'Playfair Display', serif; }

        .nf-hero {
          min-height: 74vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 160px 24px 80px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .nf-hero::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(55% 55% at 50% 20%, rgba(211,190,118,.10), transparent 70%);
          pointer-events: none;
        }
        @media (max-width: 640px) { .nf-hero { padding: 128px 20px 60px; } }

        .nf-content { position: relative; max-width: 520px; margin: 0 auto; }

        .nf-code {
          font-family: 'Playfair Display', serif;
          font-weight: 700;
          font-size: clamp(72px, 11vw, 120px);
          line-height: 1;
          color: var(--ink);
          letter-spacing: -.02em;
          margin: 20px 0 6px;
        }
        .nf-code span { color: var(--gold-d); font-style: italic; font-weight: 500; }

        .nf-title { font-size: clamp(1.4rem, 2.2vw, 1.9rem); font-weight: 600; color: var(--ink); margin: 0 0 14px; }
        .nf-text { font-size: 15px; color: var(--silver); line-height: 1.75; font-weight: 300; margin-bottom: 34px; }

        .nf-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 40px; }
        .nf-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--gold); color: var(--teal-d);
          border-radius: 5px; font-size: 12px; font-weight: 700; letter-spacing: .3px;
          padding: 14px 26px; text-decoration: none;
          transition: transform .2s, background .2s, box-shadow .2s;
          box-shadow: 0 10px 28px rgba(211,190,118,.28);
        }
        .nf-btn-primary:hover { background: #e2ce86; transform: translateY(-2px); color: var(--teal-d); }
        .nf-btn-ghost {
          display: inline-flex; align-items: center;
          background: transparent; color: var(--ink);
          border: 1px solid rgba(41,75,90,.2);
          border-radius: 5px; font-size: 12px; font-weight: 600; letter-spacing: .3px;
          padding: 14px 24px; text-decoration: none;
          transition: background .2s, border-color .2s;
        }
        .nf-btn-ghost:hover { border-color: var(--teal); background: rgba(41,75,90,.04); }

        /* Quick links — helps someone who mistyped a URL land somewhere useful */
        .nf-links { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
        .nf-link-pill {
          font-size: 12px; font-weight: 600; color: var(--teal);
          background: rgba(41,75,90,.06); border: 1px solid rgba(41,75,90,.1);
          border-radius: 30px; padding: 8px 16px; text-decoration: none;
          transition: border-color .2s, color .2s, background .2s;
        }
        .nf-link-pill:hover { border-color: var(--gold-d); color: var(--gold-d); background: rgba(211,190,118,.08); }
      `}</style>

      <div className="notfound-page">
        <Header />

        <section className="nf-hero">
          <div className="nf-content">
            <CompassIllustration />
            <div className="nf-code">4<span>0</span>4</div>
            <h1 className="nf-title">This page has wandered off the map</h1>
            <p className="nf-text">
              The page you're looking for doesn't exist, may have moved, or the link might have a typo.
              Let's get you back on route.
            </p>

            <div className="nf-actions">
              <Link to="/" className="nf-btn-primary">
                Back to Home
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </Link>
              <Link to="/contact" className="nf-btn-ghost">Contact Us</Link>
            </div>

            <div className="nf-links">
              <Link to="/search-visa" className="nf-link-pill">Visa Services</Link>
              <Link to="/trips" className="nf-link-pill">Tours & Travel</Link>
              <Link to="/car-rent" className="nf-link-pill">Car Rental</Link>
              <Link to="/faq" className="nf-link-pill">FAQ</Link>
            </div>
          </div>
        </section>

        <DuneRule />
        <DefaultFooter />
      </div>
    </>
  );
}

export default NotFound;